/**
 * Shared MapLibre wrapper for "this entity's locations, sized by signal".
 *
 * PR 5.5 of the dashboard standards. Sister component to
 * `AlertMapEmbed` (PR #27) — same OSM style, same transform helpers —
 * but takes a list of `{lat, lon, label, count}` and sizes each marker
 * by `count` so the map encodes both *where* the trouble is and *how
 * much* of it is there.
 *
 * Marker sizing
 * -------------
 * `diameter = 10 + min(count / 2, 18)` px → range [10, 28]. The cap
 * keeps a single dominant station from blowing past the popup anchor;
 * the floor keeps singletons visible at world zoom. Linear-with-cap
 * outperformed log-scale in informal testing — the alert counts on
 * Fuel Bench stations cluster in a small range (1..50ish) that doesn't
 * benefit from log compression.
 *
 * Centering / zoom
 * ----------------
 * * 0 markers → fallback (Johannesburg) + small "no data" overlay.
 * * 1 marker  → `flyTo` at zoom 14.
 * * ≥2       → `fitBounds` with 48px padding, capped at zoom 15.
 *
 * Reuses `web/src/lib/mapStyle.ts` so the basemap behaves identically
 * to every other map in the app (Maps, Alert Detail, FraudHub
 * GeoCluster).
 */

import { useEffect, useRef } from 'react';
import maplibregl, { Map as MaplibreMap, Marker, Popup } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapPinOff } from 'lucide-react';
import { OSM_STYLE_URL, transformRequest, transformStyle } from '@/lib/mapStyle';

export interface EntityLocationMarker {
  lat: number;
  lon: number;
  label: string;
  count: number;
  /** Hex (e.g. `#dc2626`). Defaults to a neutral alert-red. */
  color?: string;
}

export interface EntityLocationMapProps {
  markers: EntityLocationMarker[];
  /** CSS height. Default 240px. */
  height?: string;
}

const DEFAULT_COLOR = '#dc2626';
const FALLBACK_CENTER: [number, number] = [28.0, -26.2]; // Johannesburg

function diameterFor(count: number): number {
  return 10 + Math.min(count / 2, 18);
}

export default function EntityLocationMap({
  markers,
  height = '240px',
}: EntityLocationMapProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const markersRef = useRef<Marker[]>([]);

  // Init once; identical lifecycle to AlertMapEmbed so the two embeds
  // behave the same when both are mounted on a page.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    let initialCenter: [number, number] = FALLBACK_CENTER;
    if (markers.length > 0) {
      const avgLon = markers.reduce((s, m) => s + m.lon, 0) / markers.length;
      const avgLat = markers.reduce((s, m) => s + m.lat, 0) / markers.length;
      initialCenter = [avgLon, avgLat];
    }
    const initialZoom = markers.length === 1 ? 14 : 6;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: { version: 8, sources: {}, layers: [] },
      center: initialCenter,
      zoom: initialZoom,
      transformRequest,
      attributionControl: { compact: true },
    });
    map.setStyle(OSM_STYLE_URL, { transformStyle });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;

    const ro = new ResizeObserver(() => map.resize());
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync markers + bounds whenever the list changes. Stable identity
  // from the caller (memoise!) means this only runs when the data
  // actually changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    if (markers.length === 0) return;

    for (const m of markers) {
      const d = diameterFor(m.count);
      const el = document.createElement('div');
      el.style.cssText =
        `width:${d}px;height:${d}px;border-radius:50%;background:${m.color ?? DEFAULT_COLOR};` +
        'border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.45);cursor:pointer';
      const marker = new Marker({ element: el }).setLngLat([m.lon, m.lat]);
      marker.setPopup(
        new Popup({ offset: Math.ceil(d / 2) + 4 }).setHTML(
          `<div style="font-family:Inter,system-ui,sans-serif;font-size:12px">` +
            `<div style="font-weight:600">${escHtml(m.label)}</div>` +
            `<div style="margin-top:2px;color:#6b7280">${m.count.toLocaleString('en-ZA')} alert${m.count === 1 ? '' : 's'}</div>` +
          `</div>`,
        ),
      );
      marker.addTo(map);
      markersRef.current.push(marker);
    }

    if (markers.length === 1) {
      map.flyTo({ center: [markers[0].lon, markers[0].lat], zoom: 14, duration: 400 });
    } else if (markers.length >= 2) {
      const lons = markers.map((m) => m.lon);
      const lats = markers.map((m) => m.lat);
      map.fitBounds(
        [
          [Math.min(...lons), Math.min(...lats)],
          [Math.max(...lons), Math.max(...lats)],
        ],
        { padding: 48, duration: 400, maxZoom: 15 },
      );
    }
  }, [markers]);

  return (
    <div className="relative w-full overflow-hidden rounded-md border" style={{ height }}>
      <div ref={containerRef} className="absolute inset-0" />
      {markers.length === 0 && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted/50 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <MapPinOff className="h-4 w-4" />
            <span>No location data in this period.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function escHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
